import { useState } from "react";
import { FaSignInAlt } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import { AUTH_ERROR_MESSAGES } from "../constants/auth";
import { useAuth } from "../context/AuthContext";
import usePageTitle from "../hooks/usePageTitle";
import useScrollToTopOnMessage from "../hooks/useScrollToTopOnMessage";

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.message || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  usePageTitle("Login | Pargro");
  useScrollToTopOnMessage(error);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!username.trim() || !password) {
      setError(AUTH_ERROR_MESSAGES.credentialsRequired);
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const result = await login({
        username: username.trim(),
        password,
      });

      if (result.challenge === "NEW_PASSWORD_REQUIRED") {
        navigate("/reset-password", { replace: true });
        return;
      }

      navigate("/dashboard", { replace: true });
    } catch (loginError) {
      setError(loginError.message || AUTH_ERROR_MESSAGES.signInFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f4f7fb]">
      <header className="flex min-h-[123px] items-center justify-center border-b border-[#d9dfe8] bg-white max-[720px]:min-h-[97px]">
        <Logo />
      </header>

      <section
        className="flex justify-center px-4 py-[50px] max-[720px]:px-3 max-[720px]:py-7"
        aria-labelledby="login-title"
      >
        <form
          className="min-h-[326px] w-full max-w-[273px] rounded-[15px] bg-white px-8 pb-[31px] pt-[34px] shadow-[0_12px_30px_rgba(15,35,58,0.12)] max-[720px]:min-h-0 max-[720px]:px-5 max-[720px]:pb-[23px] max-[720px]:pt-[25px]"
          onSubmit={handleSubmit}
        >
          <h1
            id="login-title"
            className="mb-[29px] text-center text-2xl font-bold max-[720px]:mb-5 max-[720px]:text-xl"
          >
            Login
          </h1>

          <label
            className="mb-2.5 block text-base max-[720px]:text-sm"
            htmlFor="username"
          >
            Username
          </label>
          <input
            className="mb-5 h-[38px] w-full rounded-[19px] border border-[#d8dee6] bg-white px-3 text-sm outline-none transition focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.14)]"
            id="username"
            name="username"
            placeholder="Enter username or email"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />

          <label
            className="mb-2.5 block text-base max-[720px]:text-sm"
            htmlFor="password"
          >
            Password
          </label>
          <input
            className="mb-5 h-[38px] w-full rounded-[19px] border border-[#d8dee6] bg-white px-3 text-sm outline-none transition focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.14)]"
            id="password"
            name="password"
            placeholder="Enter password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          {error ? (
            <p className="-mt-3 mb-2.5 min-h-[15px] text-[0.66rem] text-[#b42318]" role="alert">
              {error}
            </p>
          ) : null}

          <button
            className="inline-flex h-[38px] w-full items-center justify-center gap-2 rounded-[19px] border-0 bg-[#1773f8] text-[0.9rem] font-medium text-white transition hover:bg-[#0d63dc] focus-visible:bg-[#0d63dc] disabled:cursor-not-allowed disabled:bg-[#8dbefb] active:translate-y-px"
            type="submit"
            disabled={isSubmitting}
          >
            <FaSignInAlt className="h-3.5 w-3.5" aria-hidden="true" />
            {isSubmitting ? "Signing in" : "Login"}
          </button>
        </form>
      </section>
    </main>
  );
}

export default LoginPage;
